import * as React from 'react';
import { Bell, SlidersHorizontal } from '@liveauctioneers/hammer-ui-core/icons';
import { Drawer } from '@liveauctioneers/hammer-ui-core/drawer';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getInitialWidth } from '@/redux/modules/windowSize';
import { getMyBidsUrl, getNotificationsUrl, getSavedItemsUrl } from '@/utils/urls';
import { HowToWinBannerContainer } from '@/components/HowToWinBanner';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { LeaderBoardAdTypes } from '@/redux/modules/catalog/ad/catalogAd.types';
import { MyItemsTab } from '../../types/MyItemsPage.types';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { useAppSelector } from '@/redux/hooks';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import CategoryAd from '@/components/Ads/CategoryAd';
import DismissableGetMobileBannerAlt from '@/components/DismissableGetMobileBanner/DismissableGetMobileBannerAlt';
import ManageButton from '@/components/Button/ManageButton';
import MyFavoritesSlideMenu from '@/pages/MyItems/MyFavorites/components/MyFavoritesSlideMenu/MyFavoritesSlideMenu';
import PageTitle from '@/components/PageTitle/PageTitle';
import styled, { useTheme } from 'styled-components';
import Tab from '@liveauctioneers/caterwaul-components/lib/Tab/Tab';
import Tabs from '@liveauctioneers/caterwaul-components/lib/Tab/Tabs';
import useIsWiderThan from '@/hooks/useIsWiderThan';

type Props = {
    children: React.ReactNode;
    tab: MyItemsTab;
};

/**
 * The "my items" page is actually two different pages: "my favorites" and "my bids"
 * where the routing to either page is controlled by tabs
 */
const MyItemsPageContainer = ({ children, tab }: Props) => {
    const {
        breakpoints: { tabletWidthNum },
    } = useTheme();
    const ssrWidth = useAppSelector(getInitialWidth);
    const isDesktopView = useIsWiderThan(tabletWidthNum, ssrWidth);
    const navigate = useNavigate();
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const { formatMessage } = useIntl();
    // 'favorites-page' is unfortunately the ad id
    const adId = tab === MyItemsTab.MyFavorites ? 'favorites-page' : MyItemsTab.MyBids;
    const manageUrl = getNotificationsUrl('searches');

    /**
     * Navigate to the my-bids or my-favorites via tabs
     */
    const handleTabsOnChange = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        const nextTab = e.currentTarget.name;

        if (nextTab === MyItemsTab.MyBids && tab !== MyItemsTab.MyBids) {
            navigate(getMyBidsUrl());
        } else if (nextTab === MyItemsTab.MyFavorites && tab !== MyItemsTab.MyFavorites) {
            navigate(getSavedItemsUrl());
        }
    };

    const adPageName = `leaderboard-${adId}`;

    return (
        <>
            <div className="mb-md">
                <HowToWinBannerContainer />
            </div>
            <CategoryAd
                adType={LeaderBoardAdTypes[adId]}
                pageName={adPageName}
                removeBottomMargin
            />
            <MyItemsPage>
                <StyledPageTitle title={<FormattedMessage id="myItems.title" />} />
                <StyledMobileBanner />
                <Tabs
                    onChange={handleTabsOnChange}
                    startingTabName={tab}
                >
                    <Tab
                        data-testid="favoritePageSavedItems"
                        label={<FormattedMessage id="myItems.favorites" />}
                        name={MyItemsTab.MyFavorites}
                    >
                        {tab === MyItemsTab.MyFavorites && children}
                    </Tab>
                    <Tab
                        data-testid="myBidsPage"
                        label={<FormattedMessage id="myItems.bids" />}
                        name={MyItemsTab.MyBids}
                    >
                        {tab === MyItemsTab.MyBids && children}
                    </Tab>
                    {isDesktopView && (
                        <StyledManageButtonContainer>
                            <ManageButton
                                hideManage
                                id="manageNotifications"
                                messageId="manageNotifications"
                                to={manageUrl}
                                withPopover
                            />
                        </StyledManageButtonContainer>
                    )}
                    {!isDesktopView && tab === MyItemsTab.MyFavorites && (
                        <StyledSlideMenu>
                            <IconButton
                                icon={SlidersHorizontal}
                                onClick={() => setIsDrawerOpen(true)}
                            />
                            <Drawer
                                actions={
                                    <SubtleMonochromeLink
                                        href={manageUrl}
                                        id="manageNotifications"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsDrawerOpen(false);
                                            window.requestAnimationFrame(() => {
                                                navigate(manageUrl);
                                            });
                                        }}
                                    >
                                        <InlineButton
                                            icon={Bell}
                                            size="sm"
                                            style="neutral"
                                        >
                                            <FormattedMessage id="manageNotifications" />
                                        </InlineButton>
                                    </SubtleMonochromeLink>
                                }
                                anchor="right"
                                closeOnOutsideClick
                                density="comfortable"
                                handleClose={() => setIsDrawerOpen(false)}
                                hasShadow
                                headerText={formatMessage({ id: 'favorites.favorite_items' })}
                                show={isDrawerOpen}
                                size="narrow"
                            >
                                <MyFavoritesSlideMenu />
                            </Drawer>
                        </StyledSlideMenu>
                    )}
                </Tabs>
            </MyItemsPage>
        </>
    );
};

export default MyItemsPageContainer;

const MyItemsPage = styled.div`
    display: flex;
    flex-direction: column;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    align-self: center;
    width: 100%;
    position: relative;
    margin: 0 auto;
    padding: 0 16px;
`;

const StyledMobileBanner = styled(DismissableGetMobileBannerAlt)`
    margin-bottom: 24px;
`;

const StyledManageButtonContainer = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: flex-end;
    align-items: center;
`;

const StyledPageTitle = styled(PageTitle)`
    && {
        margin: 32px 0;
    }
`;

const StyledSlideMenu = styled.div`
    display: flex;
    flex-grow: 1;
    justify-content: flex-end;
    align-items: center;
`;
